import React from 'react';
import { useNavigate } from 'react-router-dom';
import useComparePins, { MAX_PINS } from '../hooks/useComparePins';

// ComparePinBar — sticky bar floating above the bottom of the
// viewport whenever there's ≥1 parcel in the compare pin set.
// Surfaces the pins as chips with an X-to-unpin, plus a "Compare
// N →" CTA that routes to /compare with the pinned ids.
//
// Rendered globally in App.js so it's available from anywhere the
// user might pin a parcel — the standalone /properties page, the
// MapListPanel in split view, and (after #147 slice 2 wiring) the
// ParcelDetail action bar.
//
// Hidden entirely when there are no pins; takes zero layout space
// at that state so existing page layouts are unaffected.
function ComparePinBar() {
  const { pins, unpin, clear } = useComparePins();
  const navigate = useNavigate();

  if (pins.length === 0) return null;

  const onCompare = () => {
    if (pins.length < 2) {
      // /compare without 2+ parcels is meaningless; nudge but don't block.
      // eslint-disable-next-line no-alert
      alert('Pin at least 2 parcels to compare.');
      return;
    }
    // Spec (#789) uses `?parcels=`; the Compare page reads either
    // `?parcels=` or the legacy `?ids=` so shared URLs from either
    // shape still work.
    navigate(`/compare?parcels=${pins.map(encodeURIComponent).join(',')}`);
  };

  return (
    <div
      className="compare-pin-bar"
      role="region"
      aria-label="Comparison pin bar"
      style={{
        position: 'fixed',
        bottom: 0, left: 0, right: 0,
        zIndex: 1500,
        background: '#fff',
        borderTop: '2px solid #1976d2',
        padding: '8px 12px',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.15)',
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        flexWrap: 'wrap',
        fontSize: '0.85rem',
      }}
    >
      <span style={{ fontWeight: 600, color: '#1976d2' }}>
        Compare ({pins.length}{pins.length >= MAX_PINS ? ` / ${MAX_PINS} max` : ''})
      </span>
      {pins.map((id) => (
        <span
          key={id}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 4px 2px 8px',
            background: '#e3f2fd',
            border: '1px solid #1976d2',
            borderRadius: 12,
            fontSize: '0.78rem',
          }}
        >
          <span style={{ color: '#1976d2' }}>{id}</span>
          <button
            type="button"
            onClick={() => unpin(id)}
            aria-label={`Unpin ${id}`}
            title={`Unpin ${id}`}
            style={{
              background: 'none', border: 'none',
              cursor: 'pointer', padding: '0 2px',
              color: '#666', fontSize: '0.9rem', lineHeight: 1,
            }}
          >
            ×
          </button>
        </span>
      ))}
      <div style={{ flex: 1 }} />
      <button
        type="button"
        onClick={onCompare}
        disabled={pins.length < 2}
        style={{
          padding: '6px 14px',
          background: pins.length >= 2 ? '#1976d2' : '#bbb',
          color: '#fff',
          border: 'none',
          borderRadius: 4,
          cursor: pins.length >= 2 ? 'pointer' : 'not-allowed',
          fontWeight: 600,
        }}
      >
        Compare {pins.length} →
      </button>
      <button
        type="button"
        onClick={clear}
        style={{
          padding: '6px 10px',
          background: 'transparent',
          border: '1px solid #ccc',
          borderRadius: 4,
          color: '#666',
          cursor: 'pointer',
          fontSize: '0.78rem',
        }}
      >
        Clear
      </button>
    </div>
  );
}

export default ComparePinBar;
